import React, { useCallback, useEffect, useRef, useState } from 'react';
import './BuildSlot.css';
import { getSlotImg, getSearchFilterTypesForSlot, SLOTS, getSlotName } from '../../core/buildUtils';
import { SLOT_IDX } from '../../core/Build';
import { Lock, LockOpen, Close, DeleteOutline, Search as SearchIcon, Block, EditOutlined } from '@mui/icons-material';
import { useOverlay } from '../../contexts/OverlayContext';
import Search from '../search/Search';
import { DENSITY_SCOPE_SLOT } from '../../core/searchConfig';
import SlotTooltip from './SlotTooltip';
import ButtonGroup from '../utils/ButtonGroup';
import SlotBlockConfirm from './SlotBlockConfirm';
import SearchService from '../../services/SearchService';
import { NOTIFICATION, useNotification } from '../NotificationSystem';
import Tooltip from '../utils/Tooltip';
import { generatePulseDelay, getPulseAnimationTotalTime } from '../../utils/animationUtils';
import { usePointerHover } from '../../hooks/usePointerHover';
import { ELEMENT_ICONS } from '../../core/weapon';
import { weaponHasNeutralDamageForMaging, weaponHasNeutralHealForMaging } from '../../core/weaponMaging';
import { isSlotMagable } from '../../core/itemMaging';
import { buildElementIconFilterCore } from '../utils/CardGradient';
import { resolveMediaUrl } from '../../utils/mediaUrl';
import MediaImg from '../utils/MediaImg';
import * as st from '../../core/st';

const TXT_NEUTRAL = { fr: 'Neutre', en: 'Neutral', de: 'Neutral', es: 'Neutral', pt: 'Neutro' };
const TXT_AIR = { fr: 'Air', en: 'Air', de: 'Luft', es: 'Aire', pt: 'Ar' };
const TXT_WATER = { fr: 'Eau', en: 'Water', de: 'Wasser', es: 'Agua', pt: 'Água' };
const TXT_FIRE = { fr: 'Feu', en: 'Fire', de: 'Feuer', es: 'Fuego', pt: 'Fogo' };
const TXT_EARTH = { fr: 'Terre', en: 'Earth', de: 'Erde', es: 'Tierra', pt: 'Terra' };

const TXT_OVERLAY_SEARCH_TITLE = {
    fr: 'Recherche •',
    en: 'Search •',
    de: 'Suche •',
    es: 'Búsqueda •',
    pt: 'Busca •',
};
const OVERLAY_SCROLL_SELECTOR = '.overlay-content';

const TXT_LOCK = { fr: 'Verrouiller', en: 'Lock', de: 'Sperren', es: 'Bloquear', pt: 'Travar' };
const TXT_UNLOCK = { fr: 'Déverrouiller', en: 'Unlock', de: 'Entsperren', es: 'Desbloquear', pt: 'Destravar' };
const TXT_REMOVE = { fr: 'Supprimer', en: 'Delete', de: 'Löschen', es: 'Eliminar', pt: 'Excluir' };
const TXT_REPLACE = { fr: 'Remplacer', en: 'Replace', de: 'Ersetzen', es: 'Reemplazar', pt: 'Substituir' };
const TXT_LOCKED_STATE = { fr: 'Emplacement verrouillé', en: 'Slot locked', de: 'Slot gesperrt', es: 'Espacio bloqueado', pt: 'Espaço travado' };

const TXT_BLOCK = { fr: 'Exclure', en: 'Block', de: 'Ausschließen', es: 'Excluir', pt: 'Excluir' };
const TXT_BLOCK_TITLE = { fr: 'Exclure l\'objet', en: 'Block item', de: 'Gegenstand ausschließen', es: 'Excluir el objeto', pt: 'Excluir o item' };
const TXT_BLOCK_ADDED = { fr: 'Ajouté à la liste d\'exclusion', en: 'Added to the exclusion list', de: 'Zur Ausschlussliste hinzugefügt', es: 'Añadido a la lista de exclusión', pt: 'Adicionado à lista de exclusão' };

const TXT_WEAPON_MAGING_TITLE = { fr: 'Forgemager l\'arme', en: 'Mage weapon', de: 'Waffe schmiedmagisch bearbeiten', es: 'Forjamagiar el arma', pt: 'Forjamagiar a arma' };
const TXT_WEAPON_MAGING_DAMAGE = { fr: 'Dommages', en: 'Damage', de: 'Schaden', es: 'Daños', pt: 'Danos' };
const TXT_EDIT = { fr: 'Modifier', en: 'Edit', de: 'Bearbeiten', es: 'Editar', pt: 'Editar' };
const TXT_DETAILS = { fr: 'Détails', en: 'Details', de: 'Details', es: 'Detalles', pt: 'Detalhes' };
const TXT_WEAPON_MAGING_HEAL = { fr: 'Soins', en: 'Heals', de: 'Heilung', es: 'Curaciones', pt: 'Curas' };

const ELEMENT_KEY_TO_ICON_ID = { earth: 1, fire: 2, water: 3, air: 4 };

// The element choices each half of a forgemage is offered, in the order they are
// shown. `null` is the weapon as forged, and it leads because that is what the
// player is undoing. The heal half wears the heal artwork rather than the
// forgemage potions: two identical grids one above the other read as a repeat.
const MAGING_CHOICE_ORDER = ['neutral', 'air', 'water', 'fire', 'earth'];
const MAGING_NEUTRAL_CHOICE = 'neutral';
const MAGING_CHOICE_LABELS = {
    neutral: TXT_NEUTRAL,
    air: TXT_AIR,
    water: TXT_WATER,
    fire: TXT_FIRE,
    earth: TXT_EARTH,
};
const MAGING_DAMAGE_ICONS = {
    neutral: ELEMENT_ICONS[0],
    air: '/img/potion_air.png',
    water: '/img/potion_eau.png',
    fire: '/img/potion_feu.png',
    earth: '/img/potion_terre.png',
};
const MAGING_HEAL_ICONS = {
    neutral: '/img/stat_soin_generique.png',
    air: '/img/stat_soin_air.png',
    water: '/img/stat_soin_eau.png',
    fire: '/img/stat_soin_feu.png',
    earth: '/img/stat_soin_terre.png',
};

/** A weapon is worth offering the forgemage overlay for if either half can be maged. */
const weaponIsMagable = (weapon) =>
    weaponHasNeutralDamageForMaging(weapon) || weaponHasNeutralHealForMaging(weapon);

/**
 * The element the slot's edit corner wears. It has room for one element
 * icon, and on a weapon that has both halves that icon is the damage one:
 * damage is what the weapon is mostly doing, so showing the heal element
 * there would name the half the player did not touch. The heal element only
 * reaches the corner on a weapon with no magable damage at all.
 */
const magedElementOf = (build, weapon) => {
    const damageEl = build?.getWeaponMagingElement?.() || null;
    if (damageEl) return damageEl;
    if (weaponHasNeutralDamageForMaging(weapon)) return null;
    return build?.getWeaponMagingHealElement?.() || null;
};

const getSlotItemSignature = (item) => {
    if (!item) return null;
    return item.id ?? item.item_id ?? item.itemId ?? item.icon ?? JSON.stringify(item.name ?? null);
};

/**
 * One hover-action button on a slot (lock / remove / block / maging).
 * Kept in this file: it's only used here, so colocating keeps the behavior
 * next to its caller. `label` doubles as tooltip text and aria-label; the
 * click always stops propagation so it never opens the slot's search overlay.
 */
const SlotActionButton = ({ label, wrapperClassName, className, onClick, disabled = false, children }) => (
    <Tooltip content={label} wrapperClassName={wrapperClassName}>
        <button
            type="button"
            className={`slot-action-btn ${className}`}
            aria-label={label}
            disabled={disabled}
            onClick={(e) => {
                e.stopPropagation();
                if (disabled) return;
                onClick();
            }}
        >
            {children}
        </button>
    </Tooltip>
);

const BuildSlot = ({
    item,
    slotId,
    name,
    onMouseEnter,
    onMouseLeave,
    onRemove,
    onLock,
    setGenConfig,
    isLocked,
    build,
    setBuild,
    lang = 'fr',
    isGenerating = false,
    isServerUpdate = false,
    canEditBuild = true,
    canEditGenerator = true,
}) => {
    const hasPointerHover = usePointerHover();
    const { showOverlay, hideOverlay } = useOverlay();
    const { showNotification } = useNotification();
    const itemAnimationSignature = getSlotItemSignature(item);
    const prevItemSignatureRef = useRef(itemAnimationSignature);
    const [shouldAnimate, setShouldAnimate] = useState(false);
    const pulseDelayRef = useRef(generatePulseDelay());
    const animationFrameRef = useRef(null);
    const animationTimerRef = useRef(null);
    // The slot overlay's opener, for the search it opens: the pinned card there
    // opens this overlay, and the overlay's Replace button opens that search.
    const openItemOverlayRef = useRef(null);
    // The build as it is now. The item overlay's Replace button keeps the search
    // opener it was drawn with, and a forgemage edit made in that overlay since
    // must still reach the pinned card.
    const latestBuildRef = useRef(build);
    latestBuildRef.current = build;

    useEffect(() => {
        if (prevItemSignatureRef.current !== itemAnimationSignature) {
            if (isServerUpdate) {
                if (animationFrameRef.current) {
                    cancelAnimationFrame(animationFrameRef.current);
                }
                if (animationTimerRef.current) {
                    clearTimeout(animationTimerRef.current);
                }
                pulseDelayRef.current = generatePulseDelay();
                setShouldAnimate(false);
                animationFrameRef.current = requestAnimationFrame(() => {
                    animationFrameRef.current = null;
                    setShouldAnimate(true);
                    animationTimerRef.current = setTimeout(() => {
                        animationTimerRef.current = null;
                        setShouldAnimate(false);
                    }, getPulseAnimationTotalTime());
                });
            }
            prevItemSignatureRef.current = itemAnimationSignature;
        }
    }, [itemAnimationSignature, isServerUpdate]);

    useEffect(() => () => {
        if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current);
        if (animationTimerRef.current) clearTimeout(animationTimerRef.current);
    }, []);

    const getImage = (it) => (it && it.icon ? resolveMediaUrl(it.icon) : '/img/stat_re_armes.png');

    const getLocalizedSlotName = useCallback(() => getSlotName(slotId, lang) || name, [slotId, lang, name]);

    const getLocalizedName = useCallback((rawName) => {
        if (!rawName) return '';
        return typeof rawName === 'object'
            ? (rawName[lang] || rawName.fr || rawName.en || '')
            : String(rawName);
    }, [lang]);

    const handleBlock = useCallback(() => {
        if (!item || !canEditBuild || !canEditGenerator || !setGenConfig || isGenerating) return;
        const itemName = getLocalizedName(item.name);
        const setId = item.item_set_id || null;
        const setName = setId ? getLocalizedName(item.item_set_name) || null : null;

        const doBlock = async (includeSet) => {
            hideOverlay();
            let itemsToBlock = [item];
            if (includeSet && setId) {
                try {
                    const set = await SearchService.getSet(setId, lang);
                    if (set && Array.isArray(set.items) && set.items.length) {
                        itemsToBlock = set.items;
                    }
                } catch (e) {
                    // Fall back to blocking just the single item.
                }
            }
            onRemove();
            setGenConfig(prev => prev.addBlacklistItems(itemsToBlock));
            const actionLabel = TXT_BLOCK_ADDED[lang] || TXT_BLOCK_ADDED.en;
            showNotification(itemName ? `${itemName} - ${actionLabel}` : actionLabel, NOTIFICATION.SUCCESS);
        };

        showOverlay([
            <div key="slot-block-hdr" className="slot-header title">
                <MediaImg src={getImage(item)} alt="" className="slot-header-icon" resolve={false} />
                <h6><span>{TXT_BLOCK_TITLE[lang] || TXT_BLOCK_TITLE.en}</span></h6>
            </div>,
            <SlotBlockConfirm
                key="slot-block-confirm"
                itemName={itemName}
                setName={setName}
                lang={lang}
                onConfirm={doBlock}
                onCancel={hideOverlay}
            />,
        ], { compact: true });
    }, [
        item,
        lang,
        canEditBuild,
        canEditGenerator,
        setGenConfig,
        isGenerating,
        getLocalizedName,
        onRemove,
        showOverlay,
        hideOverlay,
        showNotification,
    ]);

    const getSlotIcon = useCallback(() => {
        const slot = SLOTS.find((s) => s.id === slotId);
        if (slot) return slot.img;
        if (SLOT_IDX.TROPHUS.includes(slotId)) {
            const trophusSlot = SLOTS.find((s) => s.id === SLOT_IDX.TROPHUS);
            return trophusSlot ? trophusSlot.img : getSlotImg(slotId);
        }
        return getSlotImg(slotId);
    }, [slotId]);

    const computePreselectedTypes = useCallback(() => getSearchFilterTypesForSlot(slotId), [slotId]);

    const getOverlaySearchPrefix = useCallback(
        () => TXT_OVERLAY_SEARCH_TITLE[lang] || TXT_OVERLAY_SEARCH_TITLE.en,
        [lang]
    );

    const handleOpenWeaponMaging = useCallback(() => {
        if (!canEditBuild || !build || !setBuild || !item?.weapon) return;
        const t = (txt) => txt[lang] || txt.en;
        // The two halves a weapon is maged in, each offered only when the weapon
        // has a neutral line of that kind. A player who mages the heals into the
        // element they stack and the damage into one they do not turns the
        // weapon into something they hit allies with, so the choices are
        // separate rather than one element for the whole weapon.
        const halves = [
            {
                key: 'damage',
                title: t(TXT_WEAPON_MAGING_DAMAGE),
                icons: MAGING_DAMAGE_ICONS,
                offered: weaponHasNeutralDamageForMaging(item.weapon),
                selected: (b) => b.getWeaponMagingElement(),
                pick: (b, v) => b.setWeaponMagingElement(v),
            },
            {
                key: 'heal',
                title: t(TXT_WEAPON_MAGING_HEAL),
                icons: MAGING_HEAL_ICONS,
                offered: weaponHasNeutralHealForMaging(item.weapon),
                selected: (b) => b.getWeaponMagingHealElement(),
                pick: (b, v) => b.setWeaponMagingHealElement(v),
            },
        ].filter((h) => h.offered);
        if (!halves.length) return;
        const wIcon = getImage(item);
        const choiceItems = (icons) => MAGING_CHOICE_ORDER.map((key) => {
            const label = t(MAGING_CHOICE_LABELS[key]);
            return {
                value: key === MAGING_NEUTRAL_CHOICE ? null : key,
                icon: <img src={icons[key]} alt="" />,
                tooltip: label,
                ariaLabel: label,
            };
        });
        // Re-rendered from the build each pick produces, so the second half can
        // be chosen without reopening — the overlay holds the build it was
        // opened with, and would otherwise show the first pick undone.
        const render = (source) => {
            showOverlay([
                <div key="weapon-mage-hdr" className="slot-header title">
                    <MediaImg src={wIcon} alt="" className="slot-header-icon" resolve={false} />
                    <h6><span>{t(TXT_WEAPON_MAGING_TITLE)}</span></h6>
                </div>,
                ...halves.map((half) => (
                    <div key={`weapon-mage-${half.key}`} className="weapon-maging-half">
                        {/* One half on its own needs no caption: the overlay's
                            own title already says what is being chosen. */}
                        {halves.length > 1 && (
                            <span className="weapon-maging-half-title">{half.title}</span>
                        )}
                        <ButtonGroup
                            className="button-group--icons"
                            ariaLabel={half.title}
                            items={choiceItems(half.icons)}
                            value={half.selected(source)}
                            onChange={(v) => {
                                const next = half.pick(source, v);
                                setBuild(next);
                                if (halves.length > 1) render(next);
                                else hideOverlay();
                            }}
                        />
                    </div>
                )),
            ], { compact: true });
        };
        render(build);
    }, [build, canEditBuild, item, lang, setBuild, showOverlay, hideOverlay]);

    const handleOpenSearch = useCallback(() => {
        if (!canEditBuild) return;
        const current = latestBuildRef.current || build;
        const typeIds = computePreselectedTypes();
        const initialFilters = {
            limit: undefined,
            offset: 0,
            item_type_ids: typeIds,
            level_max: build?.lvl || undefined,
        };

        showOverlay([
            <div key={`slot-header-${slotId}`} className="slot-header title">
                <img src={getSlotIcon()} alt="" className="slot-header-icon slot-type-icon" />
                <h6>
                    <span>{getOverlaySearchPrefix()}</span> {getLocalizedSlotName()}
                </h6>
            </div>,
            <Search
                key={`slot-search-${slotId}`}
                lang={lang}
                initialFilters={initialFilters}
                scrollContainerSelector={OVERLAY_SCROLL_SELECTOR}
                inOverlay
                densityScope={DENSITY_SCOPE_SLOT}
                initialSearchMode={{ searchItems: true, searchSets: false }}
                disableTypeFilters={false}
                preferredSlotId={slotId}
                enableStatsQuickPanel
                // Weapons only: level says almost nothing about how hard one hits
                // for a given build, and every other slot has no damage to rank by.
                damageSortBuild={slotId === SLOT_IDX.WEAPON ? (current || null) : null}
                pinnedItem={item || null}
                pinnedWeaponMaging={slotId === SLOT_IDX.WEAPON ? (current?.getWeaponMaging?.() ?? null) : null}
                pinnedItemMaging={current?.getItemMaging?.(slotId) ?? null}
                onPinnedClick={item ? () => openItemOverlayRef.current?.() : null}
            />,
        ]);
    }, [
        slotId,
        lang,
        item,
        build,
        computePreselectedTypes,
        showOverlay,
        getLocalizedSlotName,
        getSlotIcon,
        getOverlaySearchPrefix,
        canEditBuild,
    ]);

    // What a tap opens on touch, and what clicking the hover tooltip opens on
    // desktop: the item plus the slot's own actions.
    const openItemOverlay = useCallback(() => {
        if (!item || !build) return;

        // Weapon forgemage lives here only: the slot's edit corner opens this.
        const canMageWeapon =
            slotId === SLOT_IDX.WEAPON
            && item?.weapon
            && weaponIsMagable(item.weapon)
            && typeof setBuild === 'function';
        const mageElNow = magedElementOf(build, item?.weapon);
        const magingIcon = mageElNow && ELEMENT_KEY_TO_ICON_ID[mageElNow] != null
            ? ELEMENT_ICONS[ELEMENT_KEY_TO_ICON_ID[mageElNow]]
            : ELEMENT_ICONS[0];

        const footer = canEditBuild ? (
            <div className="overlay-footer-actions slot-item-overlay-footer">
                <div className="slot-item-overlay-footer-top">
                    <button
                        type="button"
                        disabled={isGenerating}
                        onClick={() => {
                            onLock();
                            hideOverlay();
                        }}
                    >
                        {isLocked ? <LockOpen sx={{ fontSize: '1.1rem' }} /> : <Lock sx={{ fontSize: '1.1rem' }} />}
                        <span>{(isLocked ? TXT_UNLOCK : TXT_LOCK)[lang] || (isLocked ? TXT_UNLOCK : TXT_LOCK).en}</span>
                    </button>
                    <button
                        type="button"
                        disabled={isGenerating}
                        onClick={() => {
                            onRemove();
                            hideOverlay();
                        }}
                    >
                        <DeleteOutline sx={{ fontSize: '1.1rem' }} />
                        <span>{TXT_REMOVE[lang] || TXT_REMOVE.en}</span>
                    </button>
                    {canEditGenerator && (
                        <button
                            type="button"
                            disabled={isGenerating}
                            onClick={handleBlock}
                        >
                            <Block sx={{ fontSize: '1.1rem' }} />
                            <span>{TXT_BLOCK[lang] || TXT_BLOCK.en}</span>
                        </button>
                    )}
                    {canMageWeapon && (
                        <button
                            type="button"
                            disabled={isGenerating}
                            onClick={handleOpenWeaponMaging}
                        >
                            <img src={magingIcon} alt="" className="slot-weapon-maging-img" />
                            <span>{TXT_WEAPON_MAGING_TITLE[lang] || TXT_WEAPON_MAGING_TITLE.en}</span>
                        </button>
                    )}
                </div>
                <button
                    type="button"
                    className="primary alt slot-item-overlay-replace-btn"
                    disabled={isGenerating || !canEditBuild}
                    onClick={handleOpenSearch}
                >
                    <SearchIcon sx={{ fontSize: '1.1rem' }} />
                    <span>{TXT_REPLACE[lang] || TXT_REPLACE.en}</span>
                </button>
            </div>
        ) : null;

        // Forgemagie happens in place: each edit is applied to the build the
        // overlay was last drawn from and the overlay is redrawn from the
        // result, the way the weapon maging overlay keeps its two halves.
        const canMage = canEditBuild
            && !isGenerating
            && isSlotMagable(slotId)
            && typeof setBuild === 'function';
        const render = (source) => {
            const mageTo = (next) => {
                if (next === source) return;
                setBuild(next);
                render(next);
            };
            showOverlay(
                [
                    <div key={`slot-item-hdr-${slotId}`} className="slot-item-overlay-header row">
                        <img src={getSlotIcon()} alt="" className="slot-header-icon slot-type-icon" />
                        <h6 className="slot-item-overlay-title">{getLocalizedSlotName()}</h6>
                    </div>,
                    <SlotTooltip
                        key={`slot-tt-${slotId}`}
                        item={item}
                        build={source}
                        lang={lang}
                        embedded
                        weaponMaging={slotId === SLOT_IDX.WEAPON ? (source?.getWeaponMaging?.() ?? null) : null}
                        itemMaging={source?.getItemMaging?.(slotId) ?? null}
                        onMageLine={canMage
                            ? (characId, value) => mageTo(source.setItemMagingLine(slotId, characId, value))
                            : null}
                        onClearMaging={canMage ? () => mageTo(source.clearItemMaging(slotId)) : null}
                    />,
                ],
                footer ? { footer } : undefined
            );
        };
        render(build);
    }, [
        item,
        build,
        slotId,
        lang,
        isLocked,
        isGenerating,
        canEditBuild,
        canEditGenerator,
        onLock,
        onRemove,
        showOverlay,
        hideOverlay,
        getSlotIcon,
        getLocalizedSlotName,
        handleOpenSearch,
        handleBlock,
        setBuild,
        handleOpenWeaponMaging,
    ]);

    openItemOverlayRef.current = openItemOverlay;

    const handleSlotClick = useCallback(() => {
        if (item && !hasPointerHover) {
            openItemOverlay();
            return;
        }
        if (!canEditBuild) {
            if (item) openItemOverlay();
            return;
        }
        handleOpenSearch();
    }, [item, hasPointerHover, canEditBuild, openItemOverlay, handleOpenSearch]);

    // Every slot's tooltip is hoverable, and hoverable means clickable: what it
    // opens is this slot's item overlay, so the opener travels with the tooltip
    // request rather than being rebuilt page-side, where the slot's own actions
    // are not in scope. An empty slot has no tooltip to make interactive.
    const mouseEnterHandler = hasPointerHover && onMouseEnter
        ? onMouseEnter(item, {
            interactive: !!item,
            // The trophy slots sit in a row: a tooltip beside one lands on top of
            // its neighbours, and an interactive tooltip swallows their hover with
            // it. Theirs goes above instead, clear of the whole row.
            stacked: SLOT_IDX.TROPHUS.includes(slotId),
            onOpenItem: openItemOverlay,
            // Which slot, so the tooltip can show the item as forgemaged.
            slot: slotId,
        })
        : undefined;
    const mouseLeaveHandler = hasPointerHover && onMouseLeave ? onMouseLeave : undefined;

    // The edit corner wears what the item overlay would let the player change
    // and has: each AP / MP / Range exo line (generated or forged by hand), then
    // the element a weapon is maged into. Nothing changed is the plain edit icon.
    const exoStats = item && Number(build?.itemIds?.[slotId]) === Number(item.id)
        ? (build.getItemExoIds?.(slotId) || []).map((id) => st.STATS.find((s) => s.id === id)).filter(Boolean)
        : [];
    const mageEl = slotId === SLOT_IDX.WEAPON ? magedElementOf(build, item?.weapon) : null;
    const editBadges = exoStats.map((stat) => ({ key: `exo-${stat.id}`, src: stat.icon }));
    if (mageEl && ELEMENT_KEY_TO_ICON_ID[mageEl] != null) {
        editBadges.push({ key: 'mage', src: ELEMENT_ICONS[ELEMENT_KEY_TO_ICON_ID[mageEl]] });
    }
    const editText = canEditBuild ? TXT_EDIT : TXT_DETAILS;
    const editLabel = editText[lang] || editText.en;
    const lockText = isLocked ? TXT_UNLOCK : TXT_LOCK;
    const lockLabel = lockText[lang] || lockText.en;
    const itemName = item?.name && typeof item.name === 'object'
        ? item.name[lang] || item.name.fr || item.name.en
        : item?.name;
    const slotImgAlt = item ? (itemName || 'Item') : `${getLocalizedSlotName()} empty`;
    const slotImgStyle = item
        ? {
            position: 'relative',
            zIndex: 2,
            '--item-element-filter-icon': buildElementIconFilterCore(item.effects_map || {}),
        }
        : { position: 'relative', zIndex: 2 };

    return (
        <div
            className={`slot panel ${name.toLowerCase()} ${isLocked ? 'locked' : ''} ${shouldAnimate ? 'animate pulse-effect' : ''}`}
            style={shouldAnimate ? { '--pulse-delay': pulseDelayRef.current } : undefined}
            onMouseEnter={mouseEnterHandler}
            onMouseLeave={mouseLeaveHandler}
            onClick={handleSlotClick}
        >
            {hasPointerHover && canEditBuild && (
                <div className="slot-actions">
                    <SlotActionButton
                        label={lockLabel}
                        wrapperClassName="slot-action-tooltip-wrap slot-action-tooltip-wrap--left"
                        className="lock-btn"
                        onClick={onLock}
                        disabled={isGenerating}
                    >
                        <Lock sx={{ fontSize: '0.8125rem' }} />
                    </SlotActionButton>
                    {item && (
                        <SlotActionButton
                            label={TXT_REMOVE[lang] || TXT_REMOVE.en}
                            wrapperClassName="slot-action-tooltip-wrap slot-action-tooltip-wrap--right"
                            className="remove-btn"
                            onClick={onRemove}
                            disabled={isGenerating}
                        >
                            <Close sx={{ fontSize: '0.8125rem' }} />
                        </SlotActionButton>
                    )}
                </div>
            )}
            {hasPointerHover && item && canEditBuild && canEditGenerator && (
                <SlotActionButton
                    label={TXT_BLOCK[lang] || TXT_BLOCK.en}
                    wrapperClassName="slot-block-trigger-wrap"
                    className="slot-block-trigger"
                    onClick={handleBlock}
                    disabled={isGenerating}
                >
                    <Block sx={{ fontSize: '0.8125rem' }} />
                </SlotActionButton>
            )}
            {isLocked && (!hasPointerHover || !canEditBuild) && (
                <div
                    className="slot-locked-indicator"
                    role="img"
                    aria-label={TXT_LOCKED_STATE[lang] || TXT_LOCKED_STATE.en}
                >
                    <Lock sx={{ fontSize: '0.875rem' }} aria-hidden />
                </div>
            )}
            {item && build && (
                <SlotActionButton
                    label={editLabel}
                    wrapperClassName="slot-edit-trigger-wrap"
                    className={`slot-edit-trigger${editBadges.length ? ' has-badges' : ''}`}
                    onClick={openItemOverlay}
                >
                    {editBadges.map((b) => (
                        <img key={b.key} src={b.src} alt="" className="slot-edit-badge" />
                    ))}
                    <EditOutlined className="slot-edit-pencil" sx={{ fontSize: '0.8125rem' }} />
                </SlotActionButton>
            )}
            {item ? (
                <MediaImg
                    key={item.id || `item-${slotId}`}
                    className="slot-img"
                    src={getImage(item)}
                    alt={slotImgAlt}
                    style={slotImgStyle}
                    fill
                    resolve={false}
                />
            ) : (
                <img
                    key={`empty-${slotId}`}
                    className="slot-img empty"
                    src={getSlotImg(slotId)}
                    alt={slotImgAlt}
                    style={slotImgStyle}
                />
            )}
        </div>
    );
};

export default BuildSlot;
